import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MailAttachResultDto,
  MailSettingsDto,
  MailSmarthostDto,
  MailStatusDto,
  MailTestResultDto,
} from '@cpm/shared';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MailWorkspace } from '../components/mail-workspace.js';
import { Modal } from '../components/Modal.js';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { HelpLink } from '../components/help.js';
import { MailCertificatesCard } from '../components/mail-certificates.js';
import { MailAntivirusCard } from '../components/mail-antivirus.js';
import { WebmailSettingsCard } from '../components/webmail-panel.js';
import { timeAgo } from '../components/charts.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
} from '../components/ui.js';

const GIB = 1024 * 1024 * 1024;

/**
 * Mail module home (G10/G13, ADR-0019/0024): the Stalwart connection
 * with deliverability defaults and the migration IMAP principal (admin),
 * the optional smarthost relay, reconcile status with drift reporting,
 * and the one-shot attach of the Plesk mail limits the G9 import parked
 * as annotations.
 */

function ConnectionCard({ section }: { section: 'connection' | 'delivery' | 'migration' }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['mail', 'settings'],
    queryFn: () => api.get<MailSettingsDto>('/api/mail/settings'),
    retry: false,
  });
  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapMasterUser, setImapMasterUser] = useState('');
  const [imapMasterSecret, setImapMasterSecret] = useState('');
  const [storageCapacityGb, setStorageCapacityGb] = useState('');
  const [storageReserveGb, setStorageReserveGb] = useState('10');
  const [dmarcPolicy, setDmarcPolicy] = useState('');
  const [dmarcRua, setDmarcRua] = useState('');
  const [testResult, setTestResult] = useState<MailTestResultDto | null>(null);

  useEffect(() => {
    if (settings.data) {
      setBaseUrl(settings.data.baseUrl ?? '');
      setUsername(settings.data.username ?? '');
      setImapHost(settings.data.imapHost ?? '');
      setImapMasterUser(settings.data.imapMasterUser ?? '');
      setStorageCapacityGb(
        settings.data.storageCapacityBytes === null
          ? ''
          : String(settings.data.storageCapacityBytes / GIB),
      );
      setStorageReserveGb(
        settings.data.storageReserveBytes === null
          ? '10'
          : String(settings.data.storageReserveBytes / GIB),
      );
      setDmarcPolicy(settings.data.dmarcPolicy ?? '');
      setDmarcRua(settings.data.dmarcRua ?? '');
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(
        '/api/mail/settings',
        section === 'connection'
          ? {
              baseUrl,
              username: username.trim() || null,
              ...(secret ? { secret } : {}),
            }
          : section === 'delivery'
            ? {
                dmarcPolicy: dmarcPolicy || null,
                dmarcRua: dmarcRua.trim() || null,
              }
            : {
                imapHost: imapHost.trim() || null,
                imapMasterUser: imapMasterUser.trim() || null,
                ...(imapMasterSecret ? { imapMasterSecret } : {}),
                storageCapacityBytes:
                  storageCapacityGb === '' ? null : Math.round(Number(storageCapacityGb) * GIB),
                storageReserveBytes:
                  storageReserveGb === '' ? null : Math.round(Number(storageReserveGb) * GIB),
              },
      ),
    onSuccess: () => {
      setSecret('');
      setImapMasterSecret('');
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
    },
  });
  const test = useMutation({
    mutationFn: () =>
      api.post<MailTestResultDto>('/api/mail/test', {
        ...(baseUrl !== '' ? { baseUrl } : {}),
        username: username.trim() === '' ? null : username.trim(),
        ...(secret !== '' ? { secret } : {}),
      }),
    onSuccess: (result) => setTestResult(result),
  });

  if (settings.isPending) {
    return (
      <Card>
        <CardHeader
          title={
            section === 'connection'
              ? 'Stalwart connection'
              : section === 'delivery'
                ? 'Delivery defaults'
                : 'Mailbox migration'
          }
        />
        <Spinner />
      </Card>
    );
  }
  if (settings.error) return null; // not an admin

  return (
    <Card>
      <CardHeader
        title={
          section === 'connection'
            ? 'Stalwart connection'
            : section === 'delivery'
              ? 'Delivery defaults'
              : 'Mailbox migration'
        }
        description={
          section === 'connection'
            ? 'The management API endpoint and encrypted credential.'
            : undefined
        }
        actions={
          settings.data.configured ? (
            <Badge tone="green">configured</Badge>
          ) : (
            <Badge tone="yellow">not configured</Badge>
          )
        }
      />
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {section === 'connection' && (
          <>
            <div>
              <Label htmlFor="mail-base-url">Base URL</Label>
              <Input
                id="mail-base-url"
                required
                type="url"
                placeholder="https://mail.example.com"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="mail-username">Username (empty = API key auth)</Label>
                <Input
                  id="mail-username"
                  placeholder="admin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="mail-secret">
                  {settings.data.hasSecret
                    ? 'API key / password (leave empty to keep)'
                    : 'API key / password'}
                </Label>
                <Input
                  id="mail-secret"
                  type="password"
                  autoComplete="new-password"
                  required={!settings.data.hasSecret}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Recommended: create a dedicated API key in Stalwart (Settings → Authentication → API
              Keys) instead of using the admin password.
            </p>
          </>
        )}
        {section === 'delivery' && (
          <div>
            <h4 className="mb-3 text-sm font-semibold">Deliverability defaults</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="mail-dmarc-policy">Default DMARC policy</Label>
                <Select
                  id="mail-dmarc-policy"
                  value={dmarcPolicy}
                  onChange={(e) => setDmarcPolicy(e.target.value)}
                >
                  <option value="">none (migration-safe minimum)</option>
                  <option value="quarantine">quarantine</option>
                  <option value="reject">reject</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="mail-dmarc-rua">DMARC aggregate reports (rua)</Label>
                <Input
                  id="mail-dmarc-rua"
                  type="email"
                  placeholder="dmarc-reports@your-panel.tld"
                  value={dmarcRua}
                  onChange={(e) => setDmarcRua(e.target.value)}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Published as each domain's <span className="font-mono">_dmarc</span> record when the
              mail server doesn't supply one. Start at <span className="font-mono">none</span>,
              tighten once flows are verified (the deliverability check reminds you).
            </p>
          </div>
        )}
        {section === 'migration' && (
          <div>
            <h4 className="mb-3 text-sm font-semibold">Mailbox migration</h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="mail-imap-host">IMAP host (empty = base URL host)</Label>
                <Input
                  id="mail-imap-host"
                  placeholder="mail.example.com"
                  value={imapHost}
                  onChange={(e) => setImapHost(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="mail-imap-master">Migration principal</Label>
                <Input
                  id="mail-imap-master"
                  placeholder="admin"
                  value={imapMasterUser}
                  onChange={(e) => setImapMasterUser(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="mail-imap-master-secret">
                  {settings.data.hasImapMasterSecret
                    ? 'Principal password (leave empty to keep)'
                    : 'Principal password'}
                </Label>
                <Input
                  id="mail-imap-master-secret"
                  type="password"
                  autoComplete="new-password"
                  value={imapMasterSecret}
                  onChange={(e) => setImapMasterSecret(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="mail-storage-capacity">Usable mail-store capacity (GiB)</Label>
                <Input
                  id="mail-storage-capacity"
                  type="number"
                  min={1}
                  step="0.1"
                  required
                  placeholder="500"
                  value={storageCapacityGb}
                  onChange={(e) => setStorageCapacityGb(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="mail-storage-reserve">Free-space reserve (GiB)</Label>
                <Input
                  id="mail-storage-reserve"
                  type="number"
                  min={0}
                  step="0.1"
                  value={storageReserveGb}
                  onChange={(e) => setStorageReserveGb(e.target.value)}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              A Stalwart account holding the <span className="font-mono">impersonate</span>{' '}
              permission — migrations log into customer mailboxes as{' '}
              <span className="font-mono">mailbox%principal</span> without touching their passwords.
              Capacity is operator-declared because the management API reports used quota, not the
              filesystem's safely usable ceiling; preflight reserves the configured headroom.
            </p>
          </div>
        )}
        {(save.error ?? test.error) != null && <ErrorNote error={save.error ?? test.error} />}
        {testResult && (
          <div
            className={
              testResult.ok
                ? 'rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-600/10 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-inset ring-red-600/10 dark:bg-red-500/10 dark:text-red-400'
            }
          >
            {testResult.ok
              ? `Connection OK — Stalwart ${testResult.edition ?? 'unknown'} edition.`
              : `Connection failed: ${testResult.error}`}
          </div>
        )}
        <div className="flex justify-end gap-2">
          {section === 'connection' && (
            <Button
              type="button"
              variant="secondary"
              loading={test.isPending}
              onClick={() => test.mutate()}
            >
              Test connection
            </Button>
          )}
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SmarthostCard() {
  const queryClient = useQueryClient();
  const smarthost = useQuery({
    queryKey: ['mail', 'smarthost'],
    queryFn: () => api.get<MailSmarthostDto>('/api/mail/smarthost'),
    retry: false,
  });
  const [address, setAddress] = useState('');
  const [port, setPort] = useState('587');
  const [implicitTls, setImplicitTls] = useState(false);
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [spfMechanism, setSpfMechanism] = useState('');

  useEffect(() => {
    if (smarthost.data?.configured) {
      setAddress(smarthost.data.address ?? '');
      setPort(String(smarthost.data.port ?? 587));
      setImplicitTls(smarthost.data.implicitTls);
      setUsername(smarthost.data.username ?? '');
      setSpfMechanism(smarthost.data.spfMechanism ?? '');
    }
  }, [smarthost.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/api/mail/smarthost', {
        address,
        port: Number(port),
        implicitTls,
        username: username.trim() === '' ? null : username.trim(),
        ...(secret !== '' ? { secret } : {}),
        spfMechanism: spfMechanism.trim() === '' ? null : spfMechanism.trim(),
      }),
    onSuccess: () => {
      setSecret('');
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete('/api/mail/smarthost'),
    onSuccess: () => {
      setAddress('');
      setUsername('');
      setSecret('');
      setSpfMechanism('');
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
    },
  });

  if (smarthost.isPending || smarthost.error) return null; // not an admin

  return (
    <Card>
      <CardHeader
        title="Smarthost (outbound relay)"
        description="Route all outbound mail through an authenticated relay while the server's own IP reputation isn't established."
        actions={
          smarthost.data.configured ? (
            <Badge tone="blue">active</Badge>
          ) : (
            <Badge tone="gray">direct delivery</Badge>
          )
        }
      />
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Label htmlFor="sh-address">Relay host</Label>
            <Input
              id="sh-address"
              required
              placeholder="smtp.sendprovider.com"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sh-port">Port</Label>
            <Input
              id="sh-port"
              type="number"
              min={1}
              max={65535}
              value={port}
              onChange={(e) => setPort(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="sh-username">Username (optional)</Label>
            <Input
              id="sh-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sh-secret">
              {smarthost.data.hasSecret ? 'Password (leave empty to keep)' : 'Password'}
            </Label>
            <Input
              id="sh-secret"
              type="password"
              autoComplete="new-password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="sh-spf">SPF mechanism of the relay</Label>
          <Input
            id="sh-spf"
            placeholder="include:_spf.sendprovider.com"
            value={spfMechanism}
            onChange={(e) => setSpfMechanism(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Woven into every mail domain's published SPF while the smarthost is active — without it,
            relayed mail fails SPF at Gmail/Yahoo.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={implicitTls}
            onChange={(e) => setImplicitTls(e.target.checked)}
          />
          TLS on connect (SMTPS, port 465 style) instead of STARTTLS
        </label>
        {(save.error ?? remove.error) != null && <ErrorNote error={save.error ?? remove.error} />}
        <div className="flex justify-end gap-2">
          {smarthost.data.configured && (
            <Button
              type="button"
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Remove the smarthost? Outbound mail returns to direct MX delivery from this server — make sure its IP reputation and PTR are in order.',
                  )
                ) {
                  remove.mutate();
                }
              }}
            >
              Remove
            </Button>
          )}
          <Button type="submit" loading={save.isPending}>
            {smarthost.data.configured ? 'Save changes' : 'Enable smarthost'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ReconcileCard({ status }: { status: MailStatusDto }) {
  const queryClient = useQueryClient();
  const reconcile = useMutation({
    mutationFn: () => api.post('/api/mail/reconcile'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['mail'] }),
  });
  const run = status.lastReconcile;

  return (
    <Card>
      <CardHeader
        title="Reconciliation"
        description="Compares the panel's mail records with the Stalwart server, both directions."
        actions={
          <Button
            variant="secondary"
            loading={reconcile.isPending || status.running}
            disabled={!status.configured}
            onClick={() => reconcile.mutate()}
          >
            <RefreshCw className="size-4" /> Reconcile now
          </Button>
        }
      />
      <div className="px-5 py-4 text-sm">
        {reconcile.error != null && (
          <div className="mb-3">
            <ErrorNote error={reconcile.error} />
          </div>
        )}
        {!run ? (
          <p className="text-slate-500 dark:text-slate-400">No reconcile has run yet.</p>
        ) : (
          <div className="space-y-2">
            <p className="flex flex-wrap items-center gap-2">
              <Badge tone={run.status === 'success' ? 'green' : 'red'}>{run.status}</Badge>
              <span className="text-slate-500 dark:text-slate-400">
                {timeAgo(run.startedAt)}
                {run.durationMs !== null && ` · ${run.durationMs} ms`}
              </span>
            </p>
            {run.error && <ErrorNote error={new Error(run.error)} />}
            {run.stats && (
              <>
                <p className="text-slate-500 dark:text-slate-400">
                  {run.stats.domainsChecked} domains, {run.stats.mailboxesChecked} mailboxes,{' '}
                  {run.stats.forwardersChecked} forwarders checked · {run.stats.orphaned} orphaned ·{' '}
                  {run.stats.relinked} relinked · {run.stats.usageUpdated} usage updates ·{' '}
                  {run.stats.suspensionRepaired} suspension repairs
                </p>
                {run.stats.unmanagedDomains.length > 0 && (
                  <p className="rounded-lg bg-amber-50 px-4 py-3 text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
                    Unmanaged domains on the mail server (not claimed by any subscription — the
                    panel never deletes them):{' '}
                    <span className="font-mono">{run.stats.unmanagedDomains.join(', ')}</span>
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function PleskAttachCard() {
  const [result, setResult] = useState<MailAttachResultDto | null>(null);
  const attach = useMutation({
    mutationFn: () => api.post<MailAttachResultDto>('/api/mail/attach-plesk-limits'),
    onSuccess: (data) => setResult(data),
  });

  return (
    <Card>
      <CardHeader
        title="Plesk mail limits"
        description="Attach mail limits retained by imports created before direct descriptor mapping to their plans and subscriptions."
        actions={
          <Button variant="secondary" loading={attach.isPending} onClick={() => attach.mutate()}>
            Attach now
          </Button>
        }
      />
      <div className="px-5 py-4 text-sm">
        {attach.error != null && <ErrorNote error={attach.error} />}
        {!result ? (
          <p className="text-slate-500 dark:text-slate-400">
            Safe to run any time — it is idempotent. Plesk's{' '}
            <span className="font-mono">max_box</span> becomes the mailbox limit,{' '}
            <span className="font-mono">mbox_quota</span> the per-mailbox quota,{' '}
            <span className="font-mono">max_maillists</span> the forwarder limit.
          </p>
        ) : result.results.length === 0 ? (
          <p className="text-slate-500 dark:text-slate-400">
            No parked mail annotations found — either nothing was imported from Plesk yet, or the
            import carried no mail limits.
          </p>
        ) : (
          <div className="space-y-2">
            {result.dangling > 0 && (
              <p className="text-amber-600 dark:text-amber-400">
                {result.dangling} annotation(s) point at panel entities that no longer exist.
              </p>
            )}
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {result.results.map((entry) => (
                <li
                  key={`${entry.entityType}-${entry.entityId}`}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <span>
                    <Badge tone={entry.entityType === 'plan' ? 'blue' : 'gray'}>
                      {entry.entityType}
                    </Badge>{' '}
                    <span className="font-medium">{entry.name}</span>
                  </span>
                  <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                    {Object.entries(entry.applied)
                      .map(([key, value]) => `${key}=${value ?? '∞'}`)
                      .join(' ') || 'no changes'}
                    {entry.skipped.length > 0 && ` · skipped: ${entry.skipped.join(', ')}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

export function MailPage({ portal = false }: { portal?: boolean }) {
  const me = useMe();
  const [resellerHelp, setResellerHelp] = useState(false);
  const [params, setParams] = useSearchParams();
  const isAdmin = me.data?.role === 'admin';
  const panel = !portal && (isAdmin || me.data?.role === 'staff');
  const tab = panel ? (params.get('view') ?? 'domains') : 'domains';
  const section = params.get('section') ?? 'connection';
  const status = useQuery({
    queryKey: ['mail', 'status'],
    queryFn: () => api.get<MailStatusDto>('/api/mail/status'),
    enabled: panel && tab === 'operations',
    refetchInterval: 60_000,
  });
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next);
  };
  return (
    <div className="space-y-4">
      <PageHeader title="Mail" description="Domains, mailboxes, routing and delivery." />
      <div className="flex flex-wrap gap-2">
        {['domains', ...(panel ? ['operations'] : []), ...(isAdmin ? ['settings'] : [])].map(
          (value) => (
            <Button
              key={value}
              variant={tab === value ? 'primary' : 'secondary'}
              onClick={() => change('view', value)}
            >
              {value[0]!.toUpperCase() + value.slice(1)}
            </Button>
          ),
        )}
        {me.data?.role === 'reseller' ? (
          <Button variant="ghost" onClick={() => setResellerHelp(true)}>
            Mail help
          </Button>
        ) : (
          <HelpLink
            shell={portal ? 'portal' : 'panel'}
            topic={portal ? 'mail/customer-accounts' : 'mail/overview'}
          >
            Mail help
          </HelpLink>
        )}
      </div>
      <Modal title="Manage owned mail" open={resellerHelp} onClose={() => setResellerHelp(false)}>
        <div className="space-y-3 text-sm">
          <p>
            Choose an owned domain to manage mailboxes, routing and domain settings. Subscription
            and customer filters narrow the workspace.
          </p>
          <p>
            Mailbox management separates the display name, quota, password, access and preferences.
            Usage includes measurement age; failed refreshes keep the last successful sample.
            Unknown storage is not zero.
          </p>
          <p>
            Login and incoming delivery are independent. Blocking delivery permanently fails new
            mail and requires typing the address. Stored messages are retained; rejected mail cannot
            be recovered later.
          </p>
          <p>
            Subscription suspension overrides login while preserving mailbox choices. Disabled
            actions explain plan, migration and setup restrictions. Your provider manages shared
            settings and DNS publication.
          </p>
        </div>
      </Modal>
      {tab === 'domains' && <MailWorkspace portal={portal} />}
      {tab === 'operations' &&
        panel &&
        (status.isPending ? (
          <Spinner />
        ) : status.error ? (
          <ErrorNote error={status.error} />
        ) : (
          <ReconcileCard status={status.data} />
        ))}
      {tab === 'settings' && isAdmin && (
        <div className="space-y-4">
          <Label htmlFor="mail-settings-section">Settings section</Label>
          <Select
            id="mail-settings-section"
            value={section}
            onChange={(e) => change('section', e.target.value)}
          >
            {[
              ['connection', 'Connection'],
              ['delivery', 'Delivery defaults'],
              ['relay', 'Outbound relay'],
              ['webmail', 'Webmail'],
              ['certificates', 'Certificates'],
              ['antivirus', 'Antivirus'],
              ['migration', 'Migration'],
            ].map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
          {['connection', 'delivery', 'migration'].includes(section) && (
            <ConnectionCard
              key={section}
              section={section as 'connection' | 'delivery' | 'migration'}
            />
          )}
          {section === 'relay' && <SmarthostCard />}
          {section === 'webmail' && <WebmailSettingsCard />}
          {section === 'certificates' && <MailCertificatesCard />}
          {section === 'antivirus' && <MailAntivirusCard />}
          {section === 'migration' && <PleskAttachCard />}
        </div>
      )}
    </div>
  );
}
